// Kleine, feste Emoji-Auswahl für Reaktionen und Chat-Text (DESIGN §3.17 EmojiPicker). Kein
// weiteres Paket: Name + Suchbegriffe je Emoji, nach Gruppen. Emojis nur als Inhalt, nie in UI-Texten.

export interface EmojiEntry {
  char: string;
  name: string;
  /** Zusätzliche Suchbegriffe (englisch, klein). */
  keywords?: string;
}

export interface EmojiGroup {
  id: string;
  title: string;
  emojis: EmojiEntry[];
}

const e = (char: string, name: string, keywords?: string): EmojiEntry => ({ char, name, keywords });

export const EMOJI_GROUPS: EmojiGroup[] = [
  {
    id: 'smileys',
    title: 'Smileys',
    emojis: [
      e('😀', 'grinning face', 'smile happy'),
      e('😃', 'grinning face with big eyes', 'smile happy'),
      e('😄', 'grinning face with smiling eyes', 'smile happy laugh'),
      e('😁', 'beaming face', 'grin'),
      e('😆', 'grinning squinting face', 'laugh'),
      e('😅', 'grinning face with sweat', 'relief phew'),
      e('😂', 'face with tears of joy', 'laugh lol'),
      e('🙂', 'slightly smiling face', 'smile'),
      e('😉', 'winking face', 'wink'),
      e('😊', 'smiling face with smiling eyes', 'blush happy'),
      e('😍', 'smiling face with heart-eyes', 'love'),
      e('🥳', 'partying face', 'celebrate party'),
      e('😎', 'smiling face with sunglasses', 'cool'),
      e('🤩', 'star-struck', 'wow amazing'),
      e('🤔', 'thinking face', 'hmm think'),
      e('🤨', 'face with raised eyebrow', 'skeptical'),
      e('😐', 'neutral face', 'meh'),
      e('😬', 'grimacing face', 'awkward'),
      e('🙃', 'upside-down face', 'silly'),
      e('😴', 'sleeping face', 'tired zzz'),
      e('😮', 'face with open mouth', 'surprised wow'),
      e('😢', 'crying face', 'sad'),
      e('😭', 'loudly crying face', 'sad sob'),
      e('😤', 'face with steam from nose', 'frustrated'),
      e('🤯', 'exploding head', 'mind blown'),
      e('🥲', 'smiling face with tear', 'grateful'),
      e('🫡', 'saluting face', 'yes sir respect'),
      e('🤝', 'handshake', 'deal agreement'),
    ],
  },
  {
    id: 'gestures',
    title: 'Gestures',
    emojis: [
      e('👍', 'thumbs up', 'yes like approve +1'),
      e('👎', 'thumbs down', 'no dislike -1'),
      e('👏', 'clapping hands', 'applause bravo'),
      e('🙌', 'raising hands', 'hooray celebrate'),
      e('🙏', 'folded hands', 'thanks please'),
      e('👋', 'waving hand', 'hello hi bye'),
      e('✌️', 'victory hand', 'peace'),
      e('🤞', 'crossed fingers', 'luck hope'),
      e('👌', 'ok hand', 'okay perfect'),
      e('💪', 'flexed biceps', 'strong'),
      e('👀', 'eyes', 'look watching'),
      e('🫶', 'heart hands', 'love thanks'),
      e('☝️', 'index pointing up', 'this'),
      e('👉', 'backhand index pointing right', 'next'),
      e('🤷', 'person shrugging', 'dunno'),
      e('🤦', 'person facepalming', 'facepalm'),
    ],
  },
  {
    id: 'hearts',
    title: 'Hearts and symbols',
    emojis: [
      e('❤️', 'red heart', 'love'),
      e('🧡', 'orange heart', 'love'),
      e('💛', 'yellow heart', 'love'),
      e('💚', 'green heart', 'love'),
      e('💙', 'blue heart', 'love'),
      e('💜', 'purple heart', 'love'),
      e('🖤', 'black heart', 'love'),
      e('💯', 'hundred points', 'perfect 100'),
      e('✅', 'check mark button', 'done yes'),
      e('☑️', 'check box with check', 'done'),
      e('❌', 'cross mark', 'no wrong'),
      e('⚠️', 'warning', 'caution'),
      e('❗', 'exclamation mark', 'important'),
      e('❓', 'question mark', 'question'),
      e('➕', 'plus', 'add'),
      e('🔥', 'fire', 'hot lit'),
      e('✨', 'sparkles', 'new magic'),
      e('⭐', 'star', 'favourite'),
      e('⚡', 'high voltage', 'fast'),
      e('💡', 'light bulb', 'idea'),
    ],
  },
  {
    id: 'work',
    title: 'Work',
    emojis: [
      e('🎉', 'party popper', 'celebrate congrats tada'),
      e('🎯', 'direct hit', 'goal target'),
      e('🚀', 'rocket', 'launch ship'),
      e('📈', 'chart increasing', 'growth up'),
      e('📉', 'chart decreasing', 'down'),
      e('📊', 'bar chart', 'stats'),
      e('📌', 'pushpin', 'pin'),
      e('📎', 'paperclip', 'attach'),
      e('📝', 'memo', 'note write'),
      e('📅', 'calendar', 'date'),
      e('⏰', 'alarm clock', 'deadline time'),
      e('⏳', 'hourglass not done', 'waiting'),
      e('🔒', 'locked', 'private secure'),
      e('🔑', 'key', 'access'),
      e('💬', 'speech balloon', 'comment chat'),
      e('📣', 'megaphone', 'announcement'),
      e('🏆', 'trophy', 'win award'),
      e('🥇', 'first place medal', 'gold winner'),
      e('💰', 'money bag', 'bonus'),
      e('🧠', 'brain', 'smart'),
      e('🛠️', 'hammer and wrench', 'tools fix'),
      e('🐛', 'bug', 'bug issue'),
      e('☕', 'hot beverage', 'coffee break'),
      e('🍕', 'pizza', 'food lunch'),
    ],
  },
  {
    id: 'nature',
    title: 'Nature',
    emojis: [
      e('🌱', 'seedling', 'grow'),
      e('🌳', 'deciduous tree', 'tree'),
      e('🌸', 'cherry blossom', 'flower'),
      e('☀️', 'sun', 'sunny'),
      e('🌈', 'rainbow', 'rainbow'),
      e('❄️', 'snowflake', 'cold winter'),
      e('🌊', 'water wave', 'wave'),
      e('🐶', 'dog face', 'dog'),
      e('🐱', 'cat face', 'cat'),
      e('🦄', 'unicorn', 'unicorn'),
    ],
  },
];

export const ALL_EMOJIS: EmojiEntry[] = EMOJI_GROUPS.flatMap((group) => group.emojis);

/** Standard für „Frequently used“, bis der Aufrufer eigene Werte liefert. */
export const DEFAULT_FREQUENT = ['👍', '❤️', '🎉', '😂', '🙏', '👀', '🔥', '✅'];

/** Sucht in Name und Stichworten (alle Wörter müssen vorkommen). */
export function searchEmojis(query: string): EmojiEntry[] {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return ALL_EMOJIS;
  return ALL_EMOJIS.filter((entry) => {
    const hay = `${entry.name} ${entry.keywords ?? ''}`;
    return words.every((word) => hay.includes(word));
  });
}

export const emojiByChar = (char: string): EmojiEntry | undefined =>
  ALL_EMOJIS.find((entry) => entry.char === char);

/**
 * Nächster Index in einem Raster aus Abschnitten (jede Gruppe beginnt eine neue Zeile).
 * `sections` = Anzahl Einträge je Abschnitt, `cols` = Spalten. Gibt den Index für die Taste zurück
 * oder `null`, wenn die Taste nichts bewegt.
 */
export function gridMove(
  sections: number[],
  cols: number,
  index: number,
  key: string,
): number | null {
  const rows: number[][] = [];
  let start = 0;
  for (const count of sections) {
    for (let i = 0; i < count; i += cols) {
      rows.push(Array.from({ length: Math.min(cols, count - i) }, (_, k) => start + i + k));
    }
    start += count;
  }
  const total = start;
  if (total === 0) return null;
  const rowIndex = rows.findIndex((row) => row.includes(index));
  if (rowIndex < 0) return 0;
  const row = rows[rowIndex]!;
  const col = row.indexOf(index);
  switch (key) {
    case 'ArrowRight':
      return Math.min(index + 1, total - 1);
    case 'ArrowLeft':
      return Math.max(index - 1, 0);
    case 'ArrowDown': {
      const next = rows[rowIndex + 1];
      return next ? next[Math.min(col, next.length - 1)]! : index;
    }
    case 'ArrowUp': {
      const prev = rows[rowIndex - 1];
      return prev ? prev[Math.min(col, prev.length - 1)]! : index;
    }
    case 'Home':
      return row[0]!;
    case 'End':
      return row[row.length - 1]!;
    default:
      return null;
  }
}
